import * as React from 'react';
import { connect } from 'react-redux';
import { ca } from '@cian/analytics';
import { useLocation } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { TDeviceType } from "@cian/ui-kit";

import { IUser } from '../../types/user';
import { IPosts } from '../../types/posts';
import { IBreadItem } from '../../types/bread_crumb';
import { getMyBlog } from '../../reducers/modules/blogs/blogs';
import { List } from '../../components/list/list';
import { BreadCrumbs } from '../bread_crumbs/bread_crumbs';
import { Aside } from '../aside/aside';
import { setBreadCrumb } from '../../reducers/modules/bread_crumbs/bread_crumbs';
import { saveScroll } from '../../reducers/modules/scroll/scroll';
import { IScroll } from '../../types/scroll';
import { getDiscussed } from '../../reducers/modules/discussed/discussed';
import { BlogListItem } from '../../components/blog_list_item/blog_list_item';
import { scrollComments } from '../../reducers/modules/scroll_comment/scroll_comment';
import { combineDataLayerObject } from '../../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../../app/helpers/breadcrumbs_helper';
import { IApplicationState, IThunkContext, TReduxActions } from "../../types/redux";
import { IConfig } from "../../types/config";
import { IAnalytics } from "../../types/analytics";

import s from './my_blog.css';

interface IProps {
  user: IUser;
  blogs: IPosts;
  scroll: IScroll;
  config: IConfig;
  deviceType: TDeviceType;
  analytics: IAnalytics;
}

interface IDispatchProps {
  setBreadCrumb(path: IBreadItem[]): void;
  getDiscussed(regionID: string): void;
  getMyBlog(params: any): void;
  saveScroll(item: any): void;
  scrollComments(): void;
}

const BlogsContainer: React.FC<IProps & IDispatchProps> = props => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tag = query.get("tag") || '';
  const ordering = query.get("ordering") || '-date_publish';

  React.useEffect(() => {
    const {config, deviceType} = props;
    const page = query.get("page") ? parseInt(query.get("page") as string) : 1;
    const regionID = config.regionID && config.regionID !== '0' ? config.regionID : '';
    const category = query.get("category") || '';
    const params = {page, ordering, tag, category};
    const crumbs: IBreadItem[] = [
      {path: '/magazine'},
      {path: '/blogs'},
      {path: '/moi-blog'},
    ];

    if (!!tag) {
      const name = tag[0].toUpperCase() + tag.substr(1);

      crumbs.push({
        path: tag,
        url: `/moi-blog?tag=${tag}`,
        name,
      });
    } else if (!!category) {
      crumbs.push({path: category});
    }

    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(props.analytics, getBreadCrumbsNames(crumbs), deviceType));
    }

    props.getMyBlog(params);
    props.setBreadCrumb(crumbs);
    props.getDiscussed(regionID);
  }, []);

  function loadMore(pageN: number) {
    const page = pageN || 1;
    const category = query.get("category") || '';
    const params = { page, ordering, tag, category };

    props.getMyBlog(params);
  }

  function seoHideBlock() {
    return (
      <span className={s['articles__schema']}>
        <span itemProp="headline">ЦИАН - статьи о недвижимости в Москве и России</span>
        <span itemProp="description">
          ЦИАН - информационные статьи о рынке недвижимости в России.
          Статьи об аренде и продаже квартир, офисов, домов и другой недвижимости. Статьи о новостройках.
        </span>
      </span>
    );
  }

  const {
    items,
    pages_count: pagesCount,
    count: recordsCount,
  } = props.blogs;
  const { item: selectedItem } = props.scroll;

  return (
    <div className={s['articles-wrap']}>
      <div itemScope itemType="https://schema.org/Article" className={`${s['articles']} cg-col-15 cg-col-xs-15`}>
        {seoHideBlock()}
        <BreadCrumbs />
        <List
            currentUser={props.user}
            items={items}
            pagesCount={pagesCount}
            recordsCount={recordsCount}
            searchTag={tag}
            saveScroll={props.saveScroll}
            selectedItem={selectedItem}
            listItemClass={BlogListItem}
            scrollComments={props.scrollComments}
            onLoadMore={(page: number) => loadMore(page)} />
      </div>
      <Aside />
    </div>
  );
}

function mapStateToProps(state: IApplicationState): IProps {
  const {
    blogs,
    scroll,
    user,
    config,
    deviceType,
    analytics,
  } = state;

  return {
    blogs,
    scroll,
    user,
    config,
    deviceType,
    analytics,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch<IApplicationState, IThunkContext, TReduxActions>): IDispatchProps {
  return {
    setBreadCrumb: (path: IBreadItem[]) => {
      dispatch(setBreadCrumb(path));
    },
    getDiscussed: (regionID: string) => {
      dispatch(getDiscussed(regionID));
    },
    getMyBlog: (params: any) => {
      dispatch(getMyBlog(params));
    },
    saveScroll: (item: any) => {
      dispatch(saveScroll(item));
    },
    scrollComments: () => {
      dispatch(scrollComments());
    },
  }
}

export const MyBlog = connect(mapStateToProps, mapDispatchToProps)(BlogsContainer);
